import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useBusinessStore } from '../stores/business';
import { api } from '../lib/api';
import { DocumentUpload } from '../components/DocumentUpload';

interface GHLTestResult {
  success: boolean;
  configured: boolean;
  message: string;
  latency?: number;
  details?: {
    baseUrl?: string;
    locationId?: string;
    hasPitToken?: boolean;
    hasLocationId?: boolean;
  };
}

interface GHLSearchResult {
  found: boolean;
  searchParams?: {
    phone: string | null;
    email: string | null;
    locationId: string | null;
  };
  contact: {
    id: string;
    name: string | null;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    tags: string[];
  } | null;
  debug?: {
    error?: string;
  };
}

export default function BusinessSettingsPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const { businesses, fetchBusinesses, updateBusiness } = useBusinessStore();
  
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [testResult, setTestResult] = useState<GHLTestResult | null>(null);
  const [searchResult, setSearchResult] = useState<GHLSearchResult | null>(null);
  const [searchQuery, setSearchQuery] = useState({ phone: '', email: '' });
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  const [formData, setFormData] = useState({
    ghlPitToken: '',
    ghlLocationId: '',
    ghlBaseUrl: 'https://services.leadconnectorhq.com',
  });

  const business = businesses.find((b) => b.id === businessId);

  useEffect(() => {
    if (businesses.length === 0) {
      fetchBusinesses();
    }
  }, [businesses.length, fetchBusinesses]);

  useEffect(() => {
    if (business) {
      setFormData({
        ghlPitToken: business.ghlPitToken || '',
        ghlLocationId: business.ghlLocationId || '',
        ghlBaseUrl: business.ghlBaseUrl || 'https://services.leadconnectorhq.com',
      });
    }
  }, [business]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) return;

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await updateBusiness(businessId, {
        ghlPitToken: formData.ghlPitToken || null,
        ghlLocationId: formData.ghlLocationId || null,
        ghlBaseUrl: formData.ghlBaseUrl || 'https://services.leadconnectorhq.com',
      });
      setSuccessMessage('GHL settings saved successfully!');
      setTestResult(null); // Clear previous test results
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!businessId) return;

    setIsTesting(true);
    setTestResult(null);
    setError(null);

    try {
      const response = await api.post<GHLTestResult>(`/businesses/${businessId}/test-ghl`);
      setTestResult(response.data!);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to test connection');
    } finally {
      setIsTesting(false);
    }
  };

  const handleSearchContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!businessId) return;
    if (!searchQuery.phone && !searchQuery.email) {
      setError('Please enter a phone number or email to search');
      return;
    }

    setIsSearching(true);
    setSearchResult(null);
    setError(null);

    try {
      const response = await api.post<GHLSearchResult>(`/businesses/${businessId}/test-ghl/search`, {
        phone: searchQuery.phone || undefined,
        email: searchQuery.email || undefined,
      });
      setSearchResult(response.data!);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to search contact');
    } finally {
      setIsSearching(false);
    }
  };

  if (!business) {
    return (
      <div className="min-h-screen bg-surface-950 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-white mb-2">Business not found</h2>
          <Link to="/businesses" className="btn-primary">
            Go to Businesses
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-950 p-6">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-surface-400 mb-6">
          <Link to="/businesses" className="hover:text-white transition-colors">
            Businesses
          </Link>
          <span>/</span>
          <Link to={`/businesses/${businessId}/agents`} className="hover:text-white transition-colors">
            {business.name}
          </Link>
          <span>/</span>
          <span className="text-primary-400">Settings</span>
        </nav>

        {/* Header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl font-bold text-white">Business Settings</h1>
          <p className="text-surface-400 mt-1">
            Configure integrations and settings for {business.name}
          </p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="mb-6 rounded-lg bg-red-500/10 border border-red-500/20 p-4 flex items-center justify-between">
            <span className="text-red-400">{error}</span>
            <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {successMessage && (
          <div className="mb-6 rounded-lg bg-green-500/10 border border-green-500/20 p-4 flex items-center justify-between">
            <span className="text-green-400">{successMessage}</span>
            <button onClick={() => setSuccessMessage(null)} className="text-green-400 hover:text-green-300">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* GHL Configuration Card */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-700 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-white">GoHighLevel Integration</h2>
                <p className="text-sm text-surface-400">Connect to your GHL CRM for contact management</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  API Base URL
                </label>
                <input
                  type="text"
                  value={formData.ghlBaseUrl}
                  onChange={(e) => setFormData({ ...formData, ghlBaseUrl: e.target.value })}
                  className="input"
                  placeholder="https://services.leadconnectorhq.com"
                />
                <p className="text-xs text-surface-500 mt-1">
                  Usually you don't need to change this
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Private Integration Token (PIT)
                </label>
                <input
                  type="password"
                  value={formData.ghlPitToken}
                  onChange={(e) => setFormData({ ...formData, ghlPitToken: e.target.value })}
                  className="input"
                  placeholder="pit-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <p className="text-xs text-surface-500 mt-1">
                  Find this in GHL Settings → Integrations → Private Integrations
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-surface-300 mb-2">
                  Location ID
                </label>
                <input
                  type="text"
                  value={formData.ghlLocationId}
                  onChange={(e) => setFormData({ ...formData, ghlLocationId: e.target.value })}
                  className="input"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                />
                <p className="text-xs text-surface-500 mt-1">
                  Find this in GHL Settings → Business Info → Location ID
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="btn-primary"
                >
                  {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
                <button
                  type="button"
                  onClick={handleTestConnection}
                  disabled={isTesting || !formData.ghlPitToken || !formData.ghlLocationId}
                  className="btn-secondary"
                >
                  {isTesting ? 'Testing...' : 'Test Connection'}
                </button>
              </div>
            </form>

            {/* Test Result */}
            {testResult && (
              <div className={`mt-6 p-4 rounded-lg border ${
                testResult.success 
                  ? 'bg-green-500/10 border-green-500/20' 
                  : 'bg-red-500/10 border-red-500/20'
              }`}>
                <div className="flex items-start gap-3">
                  {testResult.success ? (
                    <svg className="w-5 h-5 text-green-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5 text-red-400 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  <div>
                    <p className={testResult.success ? 'text-green-400' : 'text-red-400'}>
                      {testResult.message}
                    </p>
                    {testResult.latency && (
                      <p className="text-sm text-surface-400 mt-1">
                        Response time: {testResult.latency}ms
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Knowledge Base Documents Card */}
        <div className="card mb-6">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-white">Knowledge Base</h2>
                <p className="text-sm text-surface-400">Upload documents to help Mia answer business questions</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            <DocumentUpload businessId={businessId!} />
          </div>
        </div>

        {/* Contact Search Test Card */}
        <div className="card">
          <div className="card-header">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <div>
                <h2 className="font-display text-lg font-semibold text-white">Test Contact Search</h2>
                <p className="text-sm text-surface-400">Search for a contact in your GHL CRM</p>
              </div>
            </div>
          </div>
          <div className="card-body">
            <form onSubmit={handleSearchContact} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={searchQuery.phone}
                    onChange={(e) => setSearchQuery({ ...searchQuery, phone: e.target.value })}
                    className="input"
                    placeholder="+1 555 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-surface-300 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={searchQuery.email}
                    onChange={(e) => setSearchQuery({ ...searchQuery, email: e.target.value })}
                    className="input"
                    placeholder="john@example.com"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={isSearching || (!searchQuery.phone && !searchQuery.email) || !formData.ghlPitToken}
                className="btn-primary"
              >
                {isSearching ? 'Searching...' : 'Search Contact'}
              </button>
            </form>

            {/* Search Result */}
            {searchResult && (
              <div className={`mt-6 p-4 rounded-lg border ${
                searchResult.found 
                  ? 'bg-green-500/10 border-green-500/20' 
                  : 'bg-yellow-500/10 border-yellow-500/20'
              }`}>
                {searchResult.found && searchResult.contact ? (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <svg className="w-5 h-5 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-green-400 font-medium">Contact Found!</span>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex gap-2">
                        <span className="text-surface-500 w-20">ID:</span>
                        <span className="text-white font-mono">{searchResult.contact.id}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-surface-500 w-20">Name:</span>
                        <span className="text-white">
                          {searchResult.contact.name || 
                           `${searchResult.contact.firstName || ''} ${searchResult.contact.lastName || ''}`.trim() || 
                           'N/A'}
                        </span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-surface-500 w-20">Email:</span>
                        <span className="text-white">{searchResult.contact.email || 'N/A'}</span>
                      </div>
                      <div className="flex gap-2">
                        <span className="text-surface-500 w-20">Phone:</span>
                        <span className="text-white">{searchResult.contact.phone || 'N/A'}</span>
                      </div>
                      {searchResult.contact.tags.length > 0 && (
                        <div className="flex gap-2">
                          <span className="text-surface-500 w-20">Tags:</span>
                          <div className="flex flex-wrap gap-1">
                            {searchResult.contact.tags.map((tag) => (
                              <span key={tag} className="px-2 py-0.5 rounded-full text-xs bg-surface-700 text-surface-300">
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <svg className="w-5 h-5 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <span className="text-yellow-400">No contact found with the provided information</span>
                    </div>
                    {searchResult.searchParams && (
                      <div className="mt-3 p-2 rounded bg-surface-800 text-xs">
                        <p className="text-surface-500 mb-1">Search parameters:</p>
                        <p className="text-surface-400">Phone: {searchResult.searchParams.phone || 'N/A'}</p>
                        <p className="text-surface-400">Email: {searchResult.searchParams.email || 'N/A'}</p>
                        <p className="text-surface-400">Location ID: {searchResult.searchParams.locationId || 'N/A'}</p>
                      </div>
                    )}
                    {searchResult.debug?.error && (
                      <div className="mt-2 p-2 rounded bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                        Error: {searchResult.debug.error}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Help Section */}
        <div className="mt-6 p-4 rounded-lg bg-surface-900/50 border border-surface-800">
          <h3 className="text-sm font-medium text-surface-300 mb-2">How to get your GHL credentials</h3>
          <ol className="text-sm text-surface-400 space-y-2 list-decimal list-inside">
            <li>Log in to your GoHighLevel account</li>
            <li>Go to <strong className="text-surface-300">Settings → Integrations → Private Integrations</strong></li>
            <li>Create a new Private Integration or use an existing one</li>
            <li>Copy the <strong className="text-surface-300">API Token (PIT)</strong></li>
            <li>Go to <strong className="text-surface-300">Settings → Business Info</strong> to find your <strong className="text-surface-300">Location ID</strong></li>
          </ol>
        </div>
      </div>
    </div>
  );
}

